<template>
  <div class="inmuebles">
    <v-snackbar light :timeout="3000" :value="success">
      {{ $t('property.list.notifications.updated_with_success') }}
      <template>
        <v-icon color="success" class="mt-n1">mdi-book-check-outline</v-icon>
      </template>
    </v-snackbar>

    <header class="view-header">
      <div class="view-header__text">
        <h1 class="view-header__title">Inmuebles</h1>
        <p class="view-header__sub">Tu cartera de propiedades en alquiler y venta.</p>
      </div>

      <v-btn
        v-if="$vuetify.breakpoint.mdAndUp"
        color="accent"
        depressed
        class="rounded-lg"
        @click.stop="$router.push({ name: 'CreateProperty' })"
        >
        <hero-icon :icon="PlusIcon" :size="17" class="mr-1" />
        {{ $t('create.property') }}
      </v-btn>
    </header>

    <v-tabs
      v-model="tabs"
      color="primary"
      slider-color="accent"
      background-color="transparent"
      class="inmuebles__tabs"
      >
      <v-tab>{{ $t('property.list.tabs.rent') }}</v-tab>
      <v-tab>{{ $t('property.list.tabs.sell') }}</v-tab>
    </v-tabs>

    <!-- filters -->
    <div class="inmuebles__filterbar">
      <div class="inmuebles__search">
        <hero-icon :icon="SearchIcon" :size="17" />
        <input
          v-model="search"
          type="text"
          :placeholder="$t('property.list.filters.search')"
        />
      </div>

      <button
        type="button"
        class="inmuebles__filterbtn"
        :class="{ 'inmuebles__filterbtn--on': filtersOpen }"
        @click="filtersOpen = !filtersOpen"
        >
        <hero-icon :icon="FilterIcon" :size="17" />
        <span>Filtros</span>
        <span v-if="activeFilters" class="inmuebles__filterbadge">{{ activeFilters }}</span>
      </button>
    </div>

    <div v-if="filtersOpen" class="inmuebles__filterpanel">
      <div class="inmuebles__filterlabel">{{ $t('property.list.filters.property_type') }}</div>
      <div class="inmuebles__chips">
        <button
          v-for="t in property_types"
          :key="t.value"
          type="button"
          class="inmuebles__chip"
          :class="{ 'inmuebles__chip--on': filters.propertyType.includes(t.value) }"
          @click="toggle('propertyType', t.value)"
          >{{ t.text }}</button>
      </div>

      <div class="inmuebles__filterlabel">{{ $t('property.list.filters.state') }}</div>
      <div class="inmuebles__chips">
        <button
          v-for="s in states"
          :key="s.value"
          type="button"
          class="inmuebles__chip"
          :class="{ 'inmuebles__chip--on': filters.state.includes(s.value) }"
          @click="toggle('state', s.value)"
          >{{ s.text }}</button>
      </div>

      <div class="inmuebles__filterlabel">Visibilidad</div>
      <div class="inmuebles__chips">
        <button
          type="button"
          class="inmuebles__chip"
          :class="{ 'inmuebles__chip--on': filters.disabled }"
          @click="filters.disabled = !filters.disabled"
          >Solo deshabilitados</button>
      </div>
    </div>

    <List
      ref="list"
      :key="tabs"
      @reload="reset"
      :properties="properties"
    />

    <div v-if="loading" class="inmuebles__loading">Cargando…</div>
    <div ref="sentinel" class="inmuebles__sentinel"></div>

    <v-btn
      v-if="$vuetify.breakpoint.smAndDown"
      fab
      fixed
      bottom
      right
      color="accent"
      class="inmuebles__fab"
      :aria-label="$t('create.property')"
      @click="$router.push({ name: 'CreateProperty' })"
    >
      <hero-icon :icon="PlusIcon" :size="24" />
    </v-btn>
  </div>
</template>

<script>
import { PropertyTypes } from '@/graphql/queries/properties'
import { Properties } from '@/graphql/queries/property'
import List from '@/components/properties/List'
import HeroIcon from '@/components/shared/HeroIcon'
import SearchIcon from 'heroicons/24/outline/magnifying-glass.svg'
import PlusIcon from 'heroicons/24/outline/plus.svg'
import FilterIcon from 'heroicons/24/outline/funnel.svg'

export default {
  name: 'Properties',
  data() {
    return {
      SearchIcon,
      PlusIcon,
      FilterIcon,
      filtersOpen: false,
      search: "",
      timer: null,
      tabs: 0,
      filters: {
        propertyType: [],
        state: [],
        disabled: false,
      },
      property_types: [],
      states: [
        {
          text: 'Alquilado',
          value: 'rented'
        },
        {
          text: 'Borrador',
          value: 'draft'
        },
        {
          text: 'Publicado',
          value: 'published'
        },
        {
          text: 'Reservado',
          value: 'booked'
        },
      ],
      properties: [],
      types: ["rent", "sell"],
      page: 1,
      pages: 1,
      perPage: 20,
      loading: false,
      observer: null
    }
  },

  props: {
    success: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  components: {
    List,
    HeroIcon
  },

  created () {
    this.reset()
    this.fetchTypes()
  },

  mounted () {
    // load the next page as the sentinel nears the viewport (infinite scroll)
    this.observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) this.loadMore()
      },
      { rootMargin: '400px' }
    )
    if (this.$refs.sentinel) this.observer.observe(this.$refs.sentinel)
  },

  beforeDestroy () {
    if (this.observer) this.observer.disconnect()
  },

  computed: {
    activeFilters () {
      return (
        this.filters.propertyType.length +
        this.filters.state.length +
        (this.filters.disabled ? 1 : 0)
      )
    }
  },

  methods: {
    toggle (key, value) {
      const arr = this.filters[key]
      const i = arr.indexOf(value)
      if (i >= 0) arr.splice(i, 1)
      else arr.push(value)
    },

    fetchPage (append = false) {
      this.loading = true
      this.$apollo.query({
        query: Properties,
        variables: {
          search: this.search,
          filters: {
            propertyType: this.filters.propertyType.length ? this.filters.propertyType : null,
            state: this.filters.state.length ? this.filters.state : null,
            disabled: this.filters.disabled
          },
          operationType: this.types[this.tabs],
          page: this.page,
          perPage: this.perPage
        },
        fetchPolicy: 'no-cache'
      }).then(res => {
        const list = res.data.properties.properties
        this.properties = append ? this.properties.concat(list) : list
        this.pages = res.data.properties.pages
      }).finally(() => {
        this.loading = false
        this.$nextTick(this.maybeLoadMore)
      })
    },

    reset () {
      this.page = 1
      this.fetchPage(false)
    },

    loadMore () {
      if (this.loading || this.page >= this.pages) return
      this.page += 1
      this.fetchPage(true)
    },

    // auto-fill: if the sentinel is still on-screen after a load, keep going
    maybeLoadMore () {
      const el = this.$refs.sentinel
      if (!el) return
      if (el.getBoundingClientRect().top <= window.innerHeight + 400) this.loadMore()
    },

    fetchTypes () {
      this.$apollo.query({
        query: PropertyTypes,
        variables: {
          agencyIds: [1, 2]
        }
      }).then ( res => {
        const seen = new Set()
        const types = []
        res.data.propertyTypes.forEach( type => {
          if (!type.name || seen.has(type.name)) return
          seen.add(type.name)
          types.push({ text: this.typeLabel(type.name), value: type.name })
        })
        this.property_types = types.sort((a, b) => a.text.localeCompare(b.text))
      })
    },

    typeLabel (name) {
      const key = 'property.type.' + name
      const label = this.$t(key)
      return label === key
        ? name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        : label
    },
  },
  watch: {
    tabs () {
      this.reset()
    },
    filters: {
      handler () {
        this.reset()
      },

      deep: true
    },
    search: {
      handler () {
        if (this.timer) clearTimeout(this.timer)

        this.timer = setTimeout(() => this.reset(), 500)
      }
    }
  }
}
</script>

<style scoped>
/* layout (centering/cap/padding) comes from the global .app-content in App.vue */

/* reusable view header (matches Consultas / Socials) */
.view-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}
.view-header__title {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
  color: #1a1814;
  margin: 0;
}
.view-header__sub {
  font-size: 13.5px;
  color: #756f64;
  margin: 5px 0 0;
}

/* tabs: ink labels (normal case), brand-red slider */
.inmuebles__tabs >>> .v-tabs-bar {
  background: transparent;
  border-bottom: 1px solid #ece7de;
}
.inmuebles__tabs >>> .v-tab {
  text-transform: none;
  letter-spacing: normal;
  font-size: 14.5px;
  font-weight: 500;
}

/* filters */
.inmuebles__filterbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 18px 0 12px;
}
.inmuebles__search {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  background: #fff;
  border: 1px solid #e7e2d9;
  border-radius: 10px;
  padding: 0 12px;
  height: 42px;
}
.inmuebles__search .hero-icon { color: #a39a8c; flex: none; }
.inmuebles__search input {
  border: none;
  outline: none;
  background: transparent;
  width: 100%;
  font-size: 14px;
  color: #1a1814;
}
.inmuebles__search input::placeholder { color: #a39a8c; }

/* "Filtros" toggle (mirrors Consultas) */
.inmuebles__filterbtn {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
  background: #fff;
  border: 1px solid #e7e2d9;
  border-radius: 10px;
  padding: 0 16px;
  height: 42px;
  cursor: pointer;
  font-size: 13.5px;
  font-weight: 600;
  color: #1a1814;
  transition: border-color 0.15s ease;
}
.inmuebles__filterbtn:hover,
.inmuebles__filterbtn--on { border-color: #cfc7b8; }
.inmuebles__filterbtn .hero-icon { color: #6a645a; }
.inmuebles__filterbadge {
  min-width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #b52118;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
}

.inmuebles__filterpanel {
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 12px;
  padding: 16px 18px;
  margin: 0 0 12px;
}
.inmuebles__filterlabel {
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #a39a8c;
  font-weight: 600;
  margin: 14px 0 9px;
}
.inmuebles__filterlabel:first-child { margin-top: 0; }

.inmuebles__chips { display: flex; flex-wrap: wrap; gap: 8px; }
.inmuebles__chip {
  border-radius: 999px;
  border: 1px solid #e3ddd2;
  background: #fff;
  cursor: pointer;
  font-weight: 600;
  font-size: 12.5px;
  color: #56504a;
  padding: 8px 14px;
  transition: all 0.15s ease;
}
.inmuebles__chip:hover { border-color: #cfc7b8; }
.inmuebles__chip--on { background: #1a1814; color: #fff; border-color: #1a1814; }

/* mobile create FAB */
.inmuebles__fab { z-index: 6; }
.inmuebles__fab .hero-icon { color: #fff; }

/* infinite scroll */
.inmuebles__loading {
  text-align: center;
  color: #a39a8c;
  font-size: 13px;
  padding: 14px 0 4px;
}
.inmuebles__sentinel { height: 1px; }
</style>
